import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { tenantAuthApi, tenantsApi, ApiError } from "@/lib/api";

// Local-part of an email — RFC 5321 caps at 64 chars. We additionally forbid
// whitespace and `@` so the user can't smuggle a different domain into the
// locked form.
const LOCAL_PART = /^[^\s@]+$/;

const schema = z.object({
  localPart: z
    .string()
    .trim()
    .min(1, "Email required")
    .max(64, "Email is too long")
    .regex(LOCAL_PART, "Use only the part before @"),
  password: z.string().min(1, "Password required"),
});
type FormValues = z.infer<typeof schema>;

// Generic message for both "wrong password" and "wrong tenant" cases — same
// wording on both paths so the response can't be used to enumerate which
// tenants a given email belongs to. See backend handlers/tenant_auth.go for
// the matching server-side rule.
const ACCOUNT_NOT_FOUND_MSG = "Email or password is incorrect";

export function TenantLogin({ tenantCode }: { tenantCode: string }) {
  const { signIn, signOut } = useAuth();
  const [emailDomain, setEmailDomain] = useState<string | null>(null);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [domainLoading, setDomainLoading] = useState(true);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { localPart: "", password: "" },
  });

  // Resolve the tenant's locked email domain from the subdomain in the URL.
  // The endpoint is unauthenticated; a 404 means the workspace doesn't exist
  // (or the owner hasn't finished onboarding), in which case we surface a
  // generic message and disable the form.
  useEffect(() => {
    let cancelled = false;
    setDomainLoading(true);
    setDomainError(null);
    tenantsApi
      .emailDomain(tenantCode)
      .then((res) => {
        if (cancelled) return;
        setEmailDomain(res.email_domain);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = err instanceof ApiError ? err.status : 0;
        setDomainError(
          status === 404
            ? "We couldn't find a workspace at this address."
            : "We couldn't reach VirtualDesk. Try again in a moment.",
        );
      })
      .finally(() => {
        if (!cancelled) setDomainLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  const onSubmit = async (values: FormValues) => {
    if (!emailDomain) return;
    const email = `${values.localPart.trim()}@${emailDomain}`;
    try {
      // 1) Firebase sign-in — proves the email/password pair is correct, but
      //    Firebase doesn't know about tenants.
      const user = await signIn(email, values.password);

      // 2) Cross-tenant guard — confirm the authenticated user is a member of
      //    the tenant whose subdomain we're on. The endpoint returns 404
      //    "account_not_found" on a mismatch and fires the security webhook
      //    server-side. On success we just continue — the auth state change
      //    re-renders TenantShell with the protected routes.
      const idToken = await user.getIdToken();
      try {
        await tenantAuthApi.validateMembership(tenantCode, idToken);
        toast.success("Signed in");
      } catch (err) {
        const ae = err as ApiError;
        if (ae.status === 404) {
          // Wrong tenant — sign back out of Firebase so the protected routes
          // never load and the JWT can't be reused against another tenant.
          await signOut();
          toast.error("Sign-in failed", { description: ACCOUNT_NOT_FOUND_MSG });
          return;
        }
        // Unexpected — log and surface a generic message.
        console.error("validateMembership", ae);
        await signOut();
        toast.error("Sign-in failed", { description: "Please try again in a moment." });
      }
    } catch (err) {
      const code = (err as { code?: string }).code ?? "auth/unknown";
      const friendly = friendlyFirebaseError(code);
      console.error(err);
      toast.error("Sign-in failed", { description: friendly });
    }
  };

  return (
    <div className="min-h-screen bg-brand-midnight text-brand-ink flex flex-col">
      <header className="border-b border-border/60 px-6 py-4 flex items-center justify-between">
        <Logo size="sm" />
        <span className="font-mono text-xs text-muted-foreground">{tenantCode}.virtualdesk.ai</span>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-[420px]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
                <LogIn className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>Sign in</CardTitle>
                <CardDescription>Access your workspace</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {domainError ? (
              <div className="rounded-md border border-signal-failed/30 bg-signal-failed/10 p-3 text-sm text-signal-failed">
                {domainError}
              </div>
            ) : (
              <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <LockedDomainInput
                    domain={emailDomain}
                    loading={domainLoading}
                    inputProps={{
                      autoComplete: "username",
                      autoFocus: true,
                      ...form.register("localPart"),
                    }}
                  />
                  {form.formState.errors.localPart?.message && (
                    <p className="text-xs text-signal-failed">
                      {form.formState.errors.localPart.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Password</Label>
                  <PasswordInput
                    autoComplete="current-password"
                    {...form.register("password")}
                  />
                  {form.formState.errors.password?.message && (
                    <p className="text-xs text-signal-failed">{form.formState.errors.password.message}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={form.formState.isSubmitting || domainLoading || !emailDomain}
                >
                  {form.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Sign in
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

// LockedDomainInput renders a single visual input where the trailing
// `@<domain>` is a static, non-editable addon. The form value is just the
// local-part; the parent composes the full email on submit.
function LockedDomainInput({
  domain,
  loading,
  inputProps,
}: {
  domain: string | null;
  loading: boolean;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
}) {
  const suffix = loading ? "@…" : domain ? `@${domain}` : "@";
  return (
    <div
      className={
        "flex h-10 w-full items-stretch overflow-hidden rounded-md border border-input bg-transparent " +
        "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background"
      }
    >
      <input
        {...inputProps}
        type="text"
        spellCheck={false}
        autoCapitalize="none"
        autoCorrect="off"
        inputMode="email"
        disabled={loading || !domain || inputProps.disabled}
        placeholder={loading ? "Loading…" : "you"}
        className="flex-1 min-w-0 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      />
      <span
        aria-hidden
        className="flex select-none items-center border-l border-input bg-muted/40 px-3 font-mono text-sm text-muted-foreground"
      >
        {suffix}
      </span>
    </div>
  );
}

function friendlyFirebaseError(code: string): string {
  switch (code) {
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      // Same wording as the cross-tenant case — keeps the failure modes
      // indistinguishable from the user's perspective.
      return ACCOUNT_NOT_FOUND_MSG;
    case "auth/too-many-requests":
      return "Too many attempts — try again in a few minutes";
    case "auth/network-request-failed":
      return "Network error — check your connection";
    default:
      return "Please try again";
  }
}
